/* eslint-disable sonarjs/no-duplicate-string */
import * as React from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { EMAIL_SENDING_MAX_LIMIT, Emptyable, OrganizationRoutes } from "@simplyk/common";
import router from "next/router";
import { closeSnackbar, useSnackbar } from "notistack";
import { UseFormReturn } from "react-hook-form";

import { RecaptchaAction } from "../../../enums/recaptcha";
import { useRecaptchaRetry } from "../../../hooks/useRecaptchaRetry";
import { EmailContent } from "../types";

import { AmplitudeEvents } from "@/constants/amplitude";
import { SendEmailDeliveryError } from "@/gql-types";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/hooks/useTranslate";
import { useCurrentUserContext } from "@/src/contexts/CurrentUserContext";
import { EmailEditorFormData } from "@/types/email";

type UseEmailHandlersProps = {
  form: UseFormReturn<EmailEditorFormData>;
  refetch: () => Promise<void>;
  onClose: () => void;
  emailContent: EmailContent;
  thankYouEmailId?: string;
};

export const formatEmailDeliveryUpdatePayload = (data: EmailEditorFormData) => {
  const attachment = data.attachment;

  return {
    subject: data.subject,
    body: data.body,
    senderName: data.senderName,
    ...("recipientEmails" in data && typeof data.recipientEmails !== "string"
      ? { recipientEmails: data.recipientEmails }
      : {}),
    attachmentUrl: attachment?.fileUrl || null,
  };
};

const containsDataImage = (html: string): boolean => {
  const regex = /<img[^>]*(\ssrc=["']data:|\sdata-fr-old-src=["']data:|\sdata-en-old-src=["']data:)/i;
  return regex.test(html);
};

export const useEmailEditor = ({ form, refetch, onClose, emailContent, thankYouEmailId }: UseEmailHandlersProps) => {
  const { executeTrpc } = useRecaptchaRetry();
  const utils = trpc.useUtils();

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const emailingAmplitudeParams = {
    openedFrom: emailContent.openedFrom,
    pathname: emailContent.openedFrom,
    emailType: emailContent.emailType,
    category: emailContent.emailType,
    flow: emailContent.flow,
  };
  const [isSchedulerOpen, setIsSchedulerOpen] = React.useState(false);

  const handleOpenScheduler = () => {
    setIsSchedulerOpen(true);
  };

  const handleCloseScheduler = () => {
    setIsSchedulerOpen(false);
  };

  const [loading, setLoading] = React.useState(false);
  const { currentUser } = useCurrentUserContext();

  const { mutateAsync: sendEmailDeliveryMutation } = trpc.sendEmailDelivery.useMutation();
  const { mutateAsync: updateEmailContentMutation } = trpc.updateEmailContent.useMutation();
  const { mutateAsync: updateTransactionHasBeenThanked } = trpc.updateTransactionHasBeenThanked.useMutation({});

  const redirectToDraftEmailDeliveries = React.useCallback(() => {
    closeSnackbar();
    router.push({ pathname: OrganizationRoutes.Emails, query: { drafts: true } });
  }, []);

  const redirectToEmailDeliveries = React.useCallback(() => {
    closeSnackbar();
    router.push({ pathname: OrganizationRoutes.Emails });
  }, []);

  const notifyUserWithError = React.useCallback(
    (errorCode: SendEmailDeliveryError) => {
      if (errorCode === SendEmailDeliveryError.SendingFailed) {
        enqueueSnackbar(t("dashboard", "error.sendEmail.sendingFailed"), { vibe: "danger" });
      } else if (errorCode === SendEmailDeliveryError.LimitExceeded) {
        enqueueSnackbar(t("dashboard", "error.sendEmail.limitExceeded", { limit: EMAIL_SENDING_MAX_LIMIT }), {
          vibe: "danger",
        });
      } else {
        enqueueSnackbar(t("dashboard", "error.sendEmail.unexpectedStatus"), { vibe: "danger" });
      }
    },
    [enqueueSnackbar, t]
  );

  const onSave = React.useCallback(
    async (data: EmailEditorFormData): Promise<Emptyable<string>> => {
      if (!emailContent.id) {
        return "no_email_content_id";
      }

      if (data.body && containsDataImage(data.body)) {
        enqueueSnackbar(t("dashboard", "emailDrawer.error.bodyWithImage"), { vibe: "danger", autoHideDuration: null });
        return "replace_data_image";
      }

      const payload = formatEmailDeliveryUpdatePayload(data);

      const { error: updateError } = await updateEmailContentMutation({ emailContentId: emailContent.id, ...payload });
      if (updateError) {
        return updateError.code;
      }

      await refetch();
      return null;
    },
    [emailContent.id, enqueueSnackbar, refetch, t, updateEmailContentMutation]
  );

  const handleSave = form.handleSubmit(async (data: EmailEditorFormData) => {
    logAmplitudeEvent(AmplitudeEvents.EmailEditorClickSave, emailingAmplitudeParams);
    setLoading(true);
    const errorCode = await onSave(data);
    if (errorCode) {
      setLoading(false);
      return;
    }

    enqueueSnackbar({
      vibe: "positive",
      message: t("dashboard", "email.save.success.draftSaved"),
      buttonsProps: [
        {
          key: "redirect",
          onClick: redirectToDraftEmailDeliveries,
          children: t("dashboard", "writeEmailModal.successNotification.redirectToEmailDraft"),
        },
      ],
    });

    refetch?.();
    setLoading(false);
    onClose();
  });

  const handleSendTestEmail = async (previewOrganizationUserIds?: string[]) => {
    await form.handleSubmit(async (data: EmailEditorFormData) => {
      logAmplitudeEvent(AmplitudeEvents.EmailEditorClickTest, emailingAmplitudeParams);
      if (!emailContent.id) {
        return;
      }

      const payload = formatEmailDeliveryUpdatePayload(data);

      const { error: updateError } = await updateEmailContentMutation({ emailContentId: emailContent.id, ...payload });
      if (updateError) {
        return;
      }

      if (!currentUser || !emailContent.id) {
        datadogLogs.logger.error(SendEmailDeliveryError.SendingFailed);
        notifyUserWithError(SendEmailDeliveryError.SendingFailed);
        return;
      }

      const { error } = await executeTrpc(
        RecaptchaAction.SendEmailDelivery,
        currentUser?.email,
        sendEmailDeliveryMutation,
        {
          emailContentId: emailContent.id,
          isPreview: true,
          previewOrganizationUserIds: previewOrganizationUserIds || undefined,
        }
      );

      if (error) {
        notifyUserWithError(error.code as SendEmailDeliveryError);
      } else {
        enqueueSnackbar(t("dashboard", "email.testEmailDelivered"), { vibe: "positive" });
      }
      refetch?.();
      setLoading(false);
    })();
  };

  const handleSchedule = form.handleSubmit(async (data: EmailEditorFormData) => {
    setLoading(true);
    const result = await onSave(data);
    if (result) {
      return;
    }

    refetch?.();
    setLoading(false);
    onClose();
  });

  const handleSendEmail = form.handleSubmit(async (data: EmailEditorFormData) => {
    logAmplitudeEvent(AmplitudeEvents.EmailSent, emailingAmplitudeParams);
    setLoading(true);
    if (!emailContent.id) {
      return;
    }

    if (data.body && containsDataImage(data.body)) {
      enqueueSnackbar(t("dashboard", "emailDrawer.error.bodyWithImage"), { vibe: "danger", autoHideDuration: null });
      return "replace_data_image";
    }

    const payload = formatEmailDeliveryUpdatePayload(data);
    const { data: res } = await updateEmailContentMutation({ emailContentId: emailContent.id, ...payload });
    if (!res) {
      return;
    }

    if (!currentUser || !emailContent.id) {
      datadogLogs.logger.error(SendEmailDeliveryError.SendingFailed);
      notifyUserWithError(SendEmailDeliveryError.SendingFailed);
      return;
    }

    const { error } = await executeTrpc(
      RecaptchaAction.SendEmailDelivery,
      currentUser?.email,
      sendEmailDeliveryMutation,
      { emailContentId: emailContent.id, isPreview: false }
    );

    if (!error) {
      enqueueSnackbar({
        vibe: "positive",
        message: t("dashboard", "email.delivered"),
        buttonsProps: [
          {
            key: "redirect",
            onClick: redirectToEmailDeliveries,
            children: t("dashboard", "writeEmailModal.successNotification.redirectToEmailDeliveries"),
            "data-test": "email-editor-redirect-to-deliveries-button",
          },
        ],
      });
    } else {
      notifyUserWithError(error?.code as SendEmailDeliveryError);
      return error?.code;
    }

    if (thankYouEmailId) {
      await updateTransactionHasBeenThanked({ transactionId: thankYouEmailId });
      utils.getCampaignPayments.invalidate();
    }

    refetch?.();
    setLoading(false);
    onClose();
  });

  return {
    onSave,
    handleSave,
    handleSendTestEmail,
    handleSendEmail,
    handleSchedule,
    loading,
    isSchedulerOpen,
    handleOpenScheduler,
    handleCloseScheduler,
  };
};
